/**
 * Chuẩn bị ảnh bìa / logo cửa hàng ở MÁY QUẦY (v0.23.3, docs/plans/thuong-hieu-cua-hang-banner-logo-mau-nganh.md 1.1 + 4.5):
 * chủ tiệm chọn ảnh bất kỳ trong máy → máy quầy tự cắt / đặt vừa khung rồi thu nhỏ, tờ xem trước cho thấy ĐÚNG ảnh sẽ lưu.
 * Không có tờ kéo-cắt (bản đầu): bìa cắt GIỮA phủ kín 2:1 → 1200×600; logo đặt VỪA khung 512 (không cắt chữ, nền trong suốt).
 * Máy chủ vẫn mã hoá lại thành WebP (bỏ EXIF / GPS) và ép đúng cỡ nếu máy quầy cũ gửi sai — đây chỉ là bước tiện cho người dùng.
 *
 * Hàm thuần (vùng cắt, kiểm tệp, ca đủ nhẹ để gửi, đổi lỗi thành câu) có test đơn vị; phần canvas chạy trên trình duyệt và được
 * hồi quy HQ-L7-10 đo bằng ảnh thật.
 */
import { ApiFailure } from './api.ts'
import { TA } from './text/anh-cua-hang.ts'

export type LoaiAnh = 'bia' | 'logo'

/**
 * Cỡ + trần byte của ảnh SAU khi mã hoá — CHÉP từ `TRAN_ANH_CUA_HANG` (`packages/media/src/anh-cua-hang.ts`, nguồn duy nhất; DB 0100 có CHECK
 * cùng con số). Web quầy không nhập được gói `@sapa/media` (kéo `sharp` / `node:*` vào trình duyệt) nên chép; `anh-cua-hang.test.ts` so hai nơi,
 * lệch là đỏ.
 */
export const TRAN_ANH_CUA_HANG = {
  bia: { rong: 1200, cao: 600, tranBytes: 200 * 1024 },
  logo: { rong: 512, cao: 512, tranBytes: 80 * 1024 },
} as const

/** Tệp gốc chọn từ máy quá nặng thì từ chối ngay (ảnh chụp điện thoại thường 2–8 MB). */
export const TRAN_TEP_GOC_BYTES = 20 * 1024 * 1024
/** Trần ảnh gửi lên khi trình duyệt KHÔNG mã hoá được WebP (JPEG / PNG): máy chủ nhận tối đa 3 MiB (`TRAN_DAU_VAO_ANH_CUA_HANG_BYTES`) rồi mã hoá lại. */
export const TRAN_GUI_KHONG_WEBP_BYTES = 3 * 1024 * 1024
/** Thang chất lượng thử lần lượt khi mã hoá WebP ở máy quầy: lấy bản đầu tiên đủ nhẹ. */
export const THANG_CHAT_LUONG_WEBP = [0.86, 0.72, 0.6, 0.5] as const

export interface Khung {
  readonly rong: number
  readonly cao: number
}

/** Vùng cắt trên ảnh gốc (đơn vị điểm ảnh). */
export interface VungCat {
  sx: number
  sy: number
  sw: number
  sh: number
}

/** Chỗ đặt ảnh trong khung vuông (đơn vị điểm ảnh của khung). */
export interface VungDat {
  dx: number
  dy: number
  dw: number
  dh: number
}

/**
 * Vùng cắt GIỮA phủ kín khung tỉ lệ `tiLe` (= rộng / cao, bìa = 2): lấy phần lớn nhất có đúng tỉ lệ đó, nằm chính giữa ảnh.
 * Ảnh nhỏ hơn khung vẫn cho vùng đúng tỉ lệ (canvas phóng lên). Ảnh suy biến (0 điểm) → null.
 */
export function vungCatGiua(anh: Khung, tiLe: number): VungCat | null {
  if (!(anh.rong > 0) || !(anh.cao > 0) || !(tiLe > 0)) return null
  let sw: number
  let sh: number
  if (anh.rong / anh.cao > tiLe) {
    // Ảnh bè hơn khung: lấy hết chiều cao, cắt hai bên.
    sh = anh.cao
    sw = Math.min(anh.rong, Math.round(anh.cao * tiLe))
  } else {
    // Ảnh cao / vuông hơn khung: lấy hết chiều rộng, cắt trên dưới.
    sw = anh.rong
    sh = Math.min(anh.cao, Math.round(anh.rong / tiLe))
  }
  return {
    sx: Math.floor((anh.rong - sw) / 2),
    sy: Math.floor((anh.cao - sh) / 2),
    sw,
    sh,
  }
}

/** Đặt ảnh VỪA khung vuông `canh` × `canh` (không cắt, căn giữa; có thể phóng lên). Ảnh suy biến → null. */
export function vungVuaKhung(anh: Khung, canh: number): VungDat | null {
  if (!(anh.rong > 0) || !(anh.cao > 0) || !(canh > 0)) return null
  const heSo = Math.min(canh / anh.rong, canh / anh.cao)
  const dw = Math.max(1, Math.min(canh, Math.round(anh.rong * heSo)))
  const dh = Math.max(1, Math.min(canh, Math.round(anh.cao * heSo)))
  return { dx: Math.floor((canh - dw) / 2), dy: Math.floor((canh - dh) / 2), dw, dh }
}

/**
 * Chữ cái logo khi tiệm CHƯA có logo — GƯƠNG của `chuLogo` ở Mini App (`apps/miniapp/src/app.tsx`): chữ đầu của "từ đặc trưng", bỏ tiền tố chung
 * ("Tiệm Tóc Hoa Sen" → "H"), để khung xem trước ở quầy cho thấy ĐÚNG chữ khách sẽ thấy. `anh-cua-hang.test.ts` so dòng regex với nguồn Mini App.
 */
export const TU_CHUNG_TEN_TIEM =
  /^(tiệm|cửa|hàng|dịch|vụ|salon|spa|nail|nails|tóc|hair|beauty|massage|thẩm|mỹ|viện|studio|ảnh|xe|&)$/i
export function chuCaiLogo(ten: string): string {
  const tu = ten.trim().split(/\s+/)
  const dacTrung = tu.find((t) => !TU_CHUNG_TEN_TIEM.test(t)) ?? tu[0] ?? ''
  return (dacTrung[0] ?? '').toUpperCase()
}

export type MaLoiAnh = 'qua-nang-goc' | 'khong-doc' | 'khong-nen' | 'qua-chi-tiet'

/** Lỗi do ẢNH của người dùng (không phải lỗi hệ thống): có câu tiếng Việt, hiện trong thẻ. */
export class LoiAnh extends Error {
  readonly ma: MaLoiAnh
  constructor(ma: MaLoiAnh) {
    super(ma)
    this.ma = ma
  }
}

/** Tệp chọn từ máy có quá nặng không? (kiểm TRƯỚC khi đọc, để không giải mã ảnh 40 MB làm treo máy tablet cũ). */
export function kiemTraTep(tep: { size: number }): MaLoiAnh | null {
  return tep.size > TRAN_TEP_GOC_BYTES ? 'qua-nang-goc' : null
}

/** Ảnh đã mã hoá có đủ nhẹ để gửi lên không? WebP phải ≤ trần của loại; JPEG / PNG (trình duyệt không có WebP) ≤ 3 MiB — máy chủ sẽ mã hoá lại. */
export function duocGui(loai: LoaiAnh, mime: string, bytes: number): boolean {
  if (bytes <= 0) return false
  return mime === 'image/webp'
    ? bytes <= TRAN_ANH_CUA_HANG[loai].tranBytes
    : bytes <= TRAN_GUI_KHONG_WEBP_BYTES
}

/** Dạng đường dẫn ảnh cửa hàng mà máy chủ trả (cùng luật với Mini App): chỉ nhận đúng dạng, không ghép đường dẫn lạ. */
const RE_DUONG_ANH = /^\/v1\/public\/anh-cua-hang\/[0-9a-f]{32}\.webp$/
export function duongDanAnhHopLe(x: unknown): string | null {
  return typeof x === 'string' && RE_DUONG_ANH.test(x) ? x : null
}

/** Đổi mọi lỗi của luồng tải / gỡ ảnh thành MỘT câu tiếng Việt để hiện ngay trong thẻ / tờ. */
export function cauLoi(e: unknown, viec: 'tai' | 'go'): string {
  if (e instanceof LoiAnh) {
    return e.ma === 'qua-nang-goc'
      ? TA.loiQuaNangGoc
      : e.ma === 'qua-chi-tiet'
        ? TA.loiQuaChiTiet
        : e.ma === 'khong-nen'
          ? TA.loiKhongNen
          : TA.loiKhongDoc
  }
  if (e instanceof ApiFailure) {
    if (e.code === 'OFFLINE') return viec === 'tai' ? TA.loiMang : TA.loiMangGo
    if (e.code === 'FEATURE_OFF') return TA.loiChuaMo
    if (e.code === 'RATE_LIMITED') return TA.loiQuaNhieu
    if (e.status === 413) return TA.loiQuaNangMayChu
    // Lỗi của ảnh (VALIDATION): máy chủ đã nói bằng tiếng Việt ("Tệp này không phải ảnh JPG, PNG hay WebP…").
    if (e.code === 'VALIDATION' && e.messageVi.trim() !== '') return e.messageVi
  }
  return viec === 'tai' ? TA.loiChung : TA.loiGoChung
}

/** Ảnh đã sẵn sàng để xem trước + gửi. `xemTruoc` là địa chỉ `blob:` — người dùng PHẢI `URL.revokeObjectURL` khi bỏ. */
export interface AnhDaChuanBi {
  loai: LoaiAnh
  xemTruoc: string
  base64: string
  mime: string
  rong: number
  cao: number
  bytes: number
}

/**
 * Mở tệp thành ảnh điểm: `createImageBitmap` (xoay theo EXIF), không có / lỗi thì `<img>` + `decode()` (Safari < 15 không có createImageBitmap).
 * Dùng chung với ảnh chụp khách (`anh-khach.ts`) — một đường dự phòng, không nhân đôi mã. Ném `LoiAnh('khong-doc')` khi không mở được.
 */
export async function moAnh(
  tep: File,
): Promise<{ nguon: CanvasImageSource; rong: number; cao: number; dong: () => void }> {
  try {
    const bm = await createImageBitmap(tep, { imageOrientation: 'from-image' })
    return { nguon: bm, rong: bm.width, cao: bm.height, dong: () => bm.close() }
  } catch {
    // Trình duyệt cũ không nhận tuỳ chọn / định dạng: thử đường `<img>` (ảnh HEIC của iPhone do Safari giải mã được).
  }
  const url = URL.createObjectURL(tep)
  try {
    const img = new Image()
    img.decoding = 'async'
    img.src = url
    await img.decode()
    return {
      nguon: img,
      rong: img.naturalWidth,
      cao: img.naturalHeight,
      dong: () => URL.revokeObjectURL(url),
    }
  } catch {
    URL.revokeObjectURL(url)
    throw new LoiAnh('khong-doc')
  }
}

function veVaoCanvas(
  loai: LoaiAnh,
  nguon: CanvasImageSource,
  anh: Khung,
): HTMLCanvasElement | null {
  const { rong, cao } = TRAN_ANH_CUA_HANG[loai]
  const canvas = document.createElement('canvas')
  canvas.width = rong
  canvas.height = cao
  const ctx = canvas.getContext('2d')
  if (ctx === null) return null
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  if (loai === 'bia') {
    const v = vungCatGiua(anh, rong / cao)
    if (v === null) return null
    // Bìa không có trong suốt: nền trắng (ảnh PNG trong suốt không để lộ nền đen khi mã hoá JPEG).
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, rong, cao)
    ctx.drawImage(nguon, v.sx, v.sy, v.sw, v.sh, 0, 0, rong, cao)
  } else {
    const v = vungVuaKhung(anh, rong)
    if (v === null) return null
    // Logo: nền trong suốt, ảnh nằm VỪA khung (không cắt chữ).
    ctx.clearRect(0, 0, rong, cao)
    ctx.drawImage(nguon, 0, 0, anh.rong, anh.cao, v.dx, v.dy, v.dw, v.dh)
  }
  return canvas
}

function layBlob(
  canvas: HTMLCanvasElement,
  mime: string,
  chatLuong?: number,
): Promise<Blob | null> {
  return new Promise((xong) => canvas.toBlob(xong, mime, chatLuong))
}

function blobSangBase64(b: Blob): Promise<string> {
  return new Promise((xong, loi) => {
    const doc = new FileReader()
    doc.onerror = () => loi(new LoiAnh('khong-doc'))
    doc.onload = () => {
      const s = typeof doc.result === 'string' ? doc.result : ''
      xong(s.slice(s.indexOf(',') + 1))
    }
    doc.readAsDataURL(b)
  })
}

/**
 * Chuẩn bị ảnh để xem trước + gửi. Thứ tự: kiểm tệp → mở ảnh → vẽ đúng khung (bìa cắt giữa, logo vừa khung) → mã hoá WebP thang chất lượng
 * giảm dần (lấy bản đầu tiên ≤ trần); trình duyệt không mã hoá được WebP thì bìa → JPEG 0,86, logo → PNG.
 * Ném `LoiAnh` (không đọc được / quá nặng / nhiều chi tiết quá).
 */
export async function chuanBiAnh(tep: File, loai: LoaiAnh): Promise<AnhDaChuanBi> {
  const quaNang = kiemTraTep(tep)
  if (quaNang !== null) throw new LoiAnh(quaNang)
  const mo = await moAnh(tep)
  try {
    if (!(mo.rong > 0) || !(mo.cao > 0)) throw new LoiAnh('khong-doc')
    const canvas = veVaoCanvas(loai, mo.nguon, { rong: mo.rong, cao: mo.cao })
    if (canvas === null) throw new LoiAnh('khong-doc')
    let chon: Blob | null = null
    let coWebp = false
    for (const q of THANG_CHAT_LUONG_WEBP) {
      const b = await layBlob(canvas, 'image/webp', q)
      if (b === null) break
      if (b.type !== 'image/webp') break // trình duyệt không mã hoá được WebP: nó trả PNG / JPEG thay thế
      coWebp = true
      if (duocGui(loai, b.type, b.size)) {
        chon = b
        break
      }
    }
    if (chon === null && !coWebp) {
      const b = await (loai === 'bia'
        ? layBlob(canvas, 'image/jpeg', 0.86)
        : layBlob(canvas, 'image/png'))
      // Canvas đã vẽ được nhưng `toBlob` trả rỗng (thiếu bộ nhớ…): lỗi của máy, không phải của ảnh (N-GD7).
      if (b === null) throw new LoiAnh('khong-nen')
      if (!duocGui(loai, b.type, b.size)) throw new LoiAnh('qua-chi-tiet')
      chon = b
    }
    if (chon === null) throw new LoiAnh('qua-chi-tiet')
    return {
      loai,
      xemTruoc: URL.createObjectURL(chon),
      base64: await blobSangBase64(chon),
      mime: chon.type,
      rong: canvas.width,
      cao: canvas.height,
      bytes: chon.size,
    }
  } finally {
    mo.dong()
  }
}
